import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
    SortableItemUIProps,
    SortableOnDragOverEvent,
    SortableOnNavigateEvent,
    Sortable,
} from '@progress/kendo-react-sortable';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import WidgetItem from '@components-clean/core/widgets/studyDashboard-widget-item';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { WidgetIcon } from '@interfaces/widget.interface';
import { FieldDefinitionDto } from '@interfaces-clean/field-rdl-definition.interface';
import AddWidgetButton from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/add-widget-button';
import { NoWidgetsPlaceholder } from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/no-widgets-placeholder';
import WidgetConfigForm, {
    WidgetConfigFormElement,
} from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/widget-config-form';
import { WidgetDataProps } from '@pages-clean/admin/study/manage-hubs/central-monitoring/widget-config/widget-config.interface';
import { FieldDefinitionFieldType } from 'types/graphql-global-types';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ADD_CONTROLS_HUB_CONFIG_WIDGET, ADD_STUDY_DASHBOARD_CONFIG_WIDGET } from '@queries/central-monitoring';

const COMPONENT_NAME = 'widgetConfig';

interface WidgetConfigProps {
    data: WidgetDataProps[];
    isDisabled?: boolean;
    canEdit: boolean;
    studyRef: string;
    type: 'STUDY_DASHBOARD' | 'CONTROLS_HUB';
    refetch: () => Promise<void>;
}

const WidgetConfig = ({ data, isDisabled, canEdit, studyRef, type, refetch }: WidgetConfigProps) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const fieldDefinitionFields = (state?.cm2Fields as FieldDefinitionDto[])?.filter((field) => !field?.isHidden);
    const [activeConfigWidgetId, setActiveConfigWidgetId] = useState<string | number | null | undefined>(null);
    const [widgetsData, setWidgetsData] = useState<WidgetDataProps[]>([]);
    const [initialWidgetsData, setInitialWidgetsData] = useState<WidgetDataProps[]>([]);
    const isEditMode = useBoolean(false);
    const childRef = useRef<WidgetConfigFormElement>(null);
    const hasChanges = JSON.stringify(initialWidgetsData) !== JSON.stringify(widgetsData);
    const { readOnly } = useReadOnlyContext();
    const { confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const [saveWidgetConfig, { loading: dashIsLoading, error: dashIsError }] = useMutation(
        ADD_STUDY_DASHBOARD_CONFIG_WIDGET,
    );
    const [saveControlsHubWidgetConfig, { loading: chIsLoading, error: chIsError }] =
        useMutation(ADD_CONTROLS_HUB_CONFIG_WIDGET);

    const isLoading = () => {
        if (type === 'STUDY_DASHBOARD') return dashIsLoading;
        return chIsLoading;
    };

    const isError = () => {
        if (type === 'STUDY_DASHBOARD') return dashIsError;
        return chIsError;
    };

    useEffect(() => {
        isEditMode.setFalse();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setActiveConfigWidgetId(null);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    useEffect(() => {
        const newData = data?.map((widget) => {
            const strippedWidget = { ...widget };
            delete strippedWidget.__typename;
            return strippedWidget;
        });

        const dataWithConfigureIds = Array.isArray(newData)
            ? [...newData.map((widget, index) => ({ ...widget, configWidgetId: index + 1 })), { configWidgetId: 0 }]
            : [{ configWidgetId: 0 }];
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setInitialWidgetsData(dataWithConfigureIds);
    }, [data]);

    useEffect(() => {
        if (!!initialWidgetsData.length) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setWidgetsData([...initialWidgetsData]);
        }
    }, [initialWidgetsData]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, hasChanges);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [hasChanges]);

    const triggerFormValidation = (action: () => void) => {
        if (childRef?.current) {
            childRef.current
                .runFormValidation()
                .then((isValid: boolean) => {
                    if (isValid) {
                        action();
                    }
                })
                .catch((err) => console.error(err)); // eslint-disable-line no-console -- A catch is required here.
        }
    };

    const onAddWidget = () => {
        const newWidgetConfigId = widgetsData.reduce((max, obj) => Math.max(obj?.configWidgetId || 0, max), 0) + 1;

        setWidgetsData((prevWidgetsData) => [
            ...prevWidgetsData.slice(0, prevWidgetsData.length - 1),
            { configWidgetId: newWidgetConfigId },
            ...prevWidgetsData.slice(prevWidgetsData.length - 1),
        ]);

        setActiveConfigWidgetId(newWidgetConfigId);
    };

    const onDeleteWidget = (id: number | string) => {
        const updatedData = widgetsData.filter((obj) => obj.configWidgetId !== id);
        setWidgetsData([...updatedData]);
        if (activeConfigWidgetId === id) {
            setActiveConfigWidgetId(null);
        }
    };

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-nested-component-definitions
    const SortableItemUI = ({ style, attributes, dataItem: rawDataItem, forwardRef }: SortableItemUIProps) => {
        const dataItem = rawDataItem as WidgetDataProps;
        const isActiveItem = dataItem.configWidgetId === activeConfigWidgetId && isEditMode.value;
        const draggableStyle = isEditMode.value && !readOnly ? style : {};

        const combinedStyle = {
            ...draggableStyle,
        };

        if (dataItem.configWidgetId === 0 && isEditMode.value) {
            return (
                <AddWidgetButton
                    onClick={() => {
                        if (!readOnly) activeConfigWidgetId ? triggerFormValidation(onAddWidget) : onAddWidget();
                    }}
                />
            );
        }

        const returnFloatIntTitle = (fieldKey: string | null | undefined, isTooltip?: boolean) => {
            const currentFieldDefinition = fieldDefinitionFields?.find(
                (fieldDefinitionField) => fieldDefinitionField.key === fieldKey,
            );

            if (isTooltip) return currentFieldDefinition?.name;

            return currentFieldDefinition?.fieldType
                ? currentFieldDefinition?.fieldType === FieldDefinitionFieldType.FLOAT
                    ? '##.##'
                    : '####'
                : '';
        };

        if (dataItem.configWidgetId === 0 && !isEditMode.value) return null;

        return (
            <div
                key={dataItem.configWidgetId}
                ref={isEditMode.value ? forwardRef : null}
                {...attributes}
                style={combinedStyle}
                className={cn('k-mt-1 k-mr-1 k-mb-1')}
                onClick={() => {
                    if (isEditMode.value && !readOnly) {
                        if (activeConfigWidgetId) {
                            triggerFormValidation(() => setActiveConfigWidgetId(dataItem.configWidgetId));
                        } else {
                            setActiveConfigWidgetId(dataItem.configWidgetId);
                        }
                    }
                }}
                role="presentation"
            >
                <WidgetItem
                    title={dataItem.displayName}
                    titleTooltip={returnFloatIntTitle(dataItem.fieldKey, true)}
                    subtitle={returnFloatIntTitle(dataItem.fieldKey)}
                    isDraggable={isEditMode.value && !readOnly}
                    icon={dataItem.iconName as WidgetIcon}
                    onClose={() => onDeleteWidget(dataItem.configWidgetId!)}
                    data-testid={`widget-${dataItem.configWidgetId}`}
                    isActiveItem={isActiveItem}
                />
            </div>
        );
    };

    const onDragOver = (event: SortableOnDragOverEvent) => {
        setWidgetsData(event.newState as WidgetDataProps[]);
    };

    const onNavigate = (event: SortableOnNavigateEvent) => {
        setWidgetsData(event.newState as WidgetDataProps[]);
    };

    const onSave = (saveData: WidgetDataProps[], callback: () => void) => {
        if (hasChanges) {
            const promise =
                type === 'STUDY_DASHBOARD'
                    ? saveWidgetConfig({
                          variables: {
                              studyId: studyRef,
                              widgets: saveData,
                          },
                      })
                    : saveControlsHubWidgetConfig({
                          variables: {
                              studyId: studyRef,
                              widgets: saveData,
                          },
                      });
            promise
                .then(async () => {
                    ShowSuccess(t('actionsSuccessfulToast'));
                    setComponentHasChanges(COMPONENT_NAME, false);
                    callback();

                    // There's a fun edge case, when the initial data is null/empty, this means the config load query
                    // returned no config. When the save mutation happens and updates the cache, the associated load query
                    // cannot refresh itself (as it's not pointing to anything in the cache because it originally loaded a null value)
                    // This results in the widget data not updating with id's, as the useEffect on data does not fire and update the state
                    // Force a refetch on the query so that it gets tied to the cache, and subsequent updates work as expected
                    if (!data || !data.length) await refetch();
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            callback();
        }
    };

    const onWidgetsSave = () => {
        setActiveConfigWidgetId(null);
        isEditMode.setFalse();
    };

    const [currentWidget, setCurrentWidget] = useState<WidgetDataProps>();

    useEffect(() => {
        const current = widgetsData?.find((cw: WidgetDataProps) => cw.configWidgetId === activeConfigWidgetId);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setCurrentWidget(current);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [activeConfigWidgetId]);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isEditMode.value]);

    return (
        <EditCard
            title={t('common:widgets')}
            data-testid="widgets"
            onEdit={() => isEditMode.setTrue()}
            onCancel={async () => {
                if (hasChanges) {
                    const ok = await confirmDiscardChanges('confirmation-dialog-widgets');
                    if (!ok) {
                        return;
                    }

                    setActiveConfigWidgetId(null);
                    setWidgetsData([...initialWidgetsData]);
                    isEditMode.setFalse();
                } else {
                    isEditMode.setFalse();
                }
            }}
            onSave={() => {
                const saveWidgetsData = widgetsData
                    .filter((widget) => widget.configWidgetId !== 0)
                    .map(({ configWidgetId, ...rest }) => rest);

                if (activeConfigWidgetId) {
                    triggerFormValidation(() => onSave && onSave(saveWidgetsData, onWidgetsSave));
                } else {
                    onSave && onSave(saveWidgetsData, onWidgetsSave);
                }
            }}
            isEditMode={isEditMode.value}
            disabled={isDisabled || isLoading() || !canEdit}
        >
            {isLoading() && !isError() && <Loading />}
            {isError() && <ErrorMessage />}
            {!isLoading() && !isError() && (
                <>
                    <Sortable
                        idField="configWidgetId"
                        disabledField="disabled"
                        className="k-display-flex k-align-items-center k-flex-wrap k-overflow-y-auto k-gap-1"
                        data={widgetsData}
                        itemUI={SortableItemUI}
                        emptyItemUI={() =>
                            isEditMode.value ? <AddWidgetButton onClick={() => onAddWidget()} /> : null
                        }
                        onDragOver={onDragOver}
                        onNavigate={onNavigate}
                        style={{ maxHeight: '250px', margin: '-16px', padding: '16px' }}
                    />
                    {widgetsData.length === 1 && widgetsData[0].configWidgetId === 0 && !isEditMode.value && (
                        <NoWidgetsPlaceholder />
                    )}
                    {activeConfigWidgetId && isEditMode.value && (
                        <div className="k-mt-1">
                            <Card
                                title={t('common:widgetSettings', {
                                    recordId: (currentWidget?.recordId as string) ?? '',
                                })}
                                data-testid="widget-form"
                            >
                                <WidgetConfigForm
                                    ref={childRef}
                                    currentWidget={currentWidget}
                                    widgetsData={widgetsData}
                                    setWidgetsData={setWidgetsData}
                                    isDisabled={isDisabled}
                                    fieldDefinitionFields={fieldDefinitionFields}
                                    widgetType={type}
                                />
                            </Card>
                        </div>
                    )}
                </>
            )}
        </EditCard>
    );
};

export default WidgetConfig;
